import type { ErrorResponse } from '@customTypes/types';
import { notifications } from '@mantine/notifications';
import * as Sentry from '@sentry/react';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { createRoot } from 'react-dom/client';
import './index.css';

import { App } from './App';
import { errorNotificationStyleConfig } from './features/notification/config/errorNotificationConfig';
import type { AppProviderProps } from './shared/contexts';
import { AppProvider } from './shared/contexts';
import { translate } from './shared/i18n/translate';

if (import.meta.env.MODE === 'production') {
  Sentry.init({
    dsn: 'https://9d151dffa9eebd3c5bd4a8beaff85761@o4509407877726208.ingest.de.sentry.io/4509407878905936',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [Sentry.replayIntegration()],
    // Session Replay
    replaysSessionSampleRate: 1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false
    }
  },
  queryCache: new QueryCache({
    onError: (cause) => {
      const { response } = cause as AxiosError<ErrorResponse>;
      if (response?.data.Detail && response?.status !== 401) {
        return notifications.show({
          title: response?.data.Detail,
          ...errorNotificationStyleConfig
        });
      }
      notifications.show({
        title: translate('notifications.generalError'),
        ...errorNotificationStyleConfig
      });
    }
  }),
  mutationCache: new MutationCache({
    onError: (cause) => {
      const { response } = cause as AxiosError<ErrorResponse>;
      if (response?.data.Detail && response?.status !== 401) {
        return notifications.show({
          title: response?.data.Detail,
          ...errorNotificationStyleConfig
        });
      }
      notifications.show({
        title: translate('notifications.generalError'),
        ...errorNotificationStyleConfig
      });
    }
  })
});

const providerProps: Omit<AppProviderProps, 'children'> = {
  query: { client: queryClient }
  // auth: { initialState: null }
};

createRoot(document.getElementById('root')!).render(
  <AppProvider {...providerProps}>
    <App />
  </AppProvider>
);
